/** @prettier */

//-----------------------------
//	Q-ton Hyozanグラフ用データ作成処理
//-----------------------------

//interface import
import {graph} from '../define';
import * as apiIF from '../Type/api';
//type import
import {GraphFormType, GraphDataRange, GraphTargetType} from '../Type/TypeDef';
import {roundOff} from './convertNumber';
import {translateDate} from './graphFunction';
import {TFunction} from 'i18next';

interface BasicMakeModbusGraphDataArgIF {
  graphType: GraphFormType; //グラフの種類
  dateRange: GraphDataRange; //グラフ日付範囲
  graphTargetID: string; //グラフ対象のID
  graphStartDate: string; //グラフデータ取得開始日時
  graphEndDate: string; // グラフデータ取得終了日時
  timeZone: string; //タイムゾーン
  trans: TFunction;
}

interface MakeGraphDataQtonHyozanArgType extends BasicMakeModbusGraphDataArgIF {
  graphTarget: GraphTargetType; //グラフ描画対象
  rtStateData: apiIF.res.getStatesSitesRt | null; //熱源機状態データ
  selectedItem: (keyof apiIF.resParameters.rtOUStates)[]; //グラフ表示項目
}

interface MakeHyozanIUGraphDataArgIF extends BasicMakeModbusGraphDataArgIF {
  rtStateData: apiIF.res.getStatesSitesRt | null; //熱源機状態データ
  selectedItem: (keyof apiIF.resParameters.rtIUStates)[]; //グラフ表示項目
}
//データが無い場合の戻り値
const resultForNoData = {
  xLabel: [],
  yArrayBar: [],
  yArrayLine: [],
  yArrayCopLine: [],
};
/**
 * Q-ton Hyozanグラフ用データ作成
 * @param param0 param0
 * @returns グラフ描画用データ
 */
export const makeRTGraphData = ({
  graphType,
  dateRange,
  graphTargetID,
  graphStartDate,
  graphEndDate,
  timeZone,
  graphTarget,
  rtStateData,
  selectedItem,
  trans,
}: MakeGraphDataQtonHyozanArgType) => {
  let dateFormat = 'HH'; //グラフの軸表示フォーマット 初期値：1日
  /** 温度グラフのデータ取得間隔（msec） */
  let everyMsec = graph.TREND_REPORTING_EVERY_MSEC_DAY;
  if (graphType === 'line') {
    //1日or1週間の切替
    if (dateRange === 'day') {
      dateFormat = 'HH:mm';
    } else {
      dateFormat = 'MMM.dd';
      everyMsec = graph.TREND_REPORTING_EVERY_MSEC_WEEK;
    }
  } else {
    //グラフ表示期間の切替
    switch (dateRange) {
      case 'day':
        dateFormat = 'HH:mm';
        break;
      case 'week':
        dateFormat = 'MMM.dd';
        break;
      case 'month':
        dateFormat = 'dd';
        break;
      case 'year':
        dateFormat = 'MMM.';
        break;
      case 'tenYear':
        dateFormat = 'yyyy';
        break;
    }
  }

  //データが無い場合の戻り値
  const resultForNoData = {
    xLabel: [],
    yArrayLine: [],
    yArrayBarHot: [],
    yArrayBarCool: [],
    yArrayBarEnergy: [],
    yArrayCopLine: [],
  };

  //どの選択された熱源機が押されたか判定
  if (rtStateData === null || rtStateData['rt-states'].length === 0) {
    return resultForNoData;
  }
  const targetStateIndex = rtStateData['rt-states'][0][
    'outdoor-unit-states'
  ].findIndex(state => {
    return state['unit-id'] === graphTargetID;
  });
  if (targetStateIndex === -1) {
    return resultForNoData;
  }

  //グラフ表示に必要なデータのみに絞った配列
  let pickupDate = rtStateData['rt-states'].map(info => {
    return {
      date: info.date,
      states: info['outdoor-unit-states'],
    };
  });
  //グラフデータの先頭がグラフ取得開始日でない場合は、配列を反転する
  if (pickupDate[0].date !== graphStartDate) {
    pickupDate.reverse();
  }

  //グラフ作成ライブラリが読み取れる形に整形
  let xLabel = [];
  //折れ線グラフの場合
  if (graphType === 'line') {
    //温度グラフデータの作成
    let yData = selectedItem.map(item => ({
      name: item,
      data: [] as (number | null)[],
    }));
    /** データ存在確認用の日時（unixTime） */
    let confirmationDate = new Date(graphStartDate).getTime();
    /** 時間情報とnullデータを追加し、確認用の日時をデータ取得間隔分進める処理 */
    const addMissingData = () => {
      //時間情報の追加
      xLabel.push(
        translateDate(new Date(confirmationDate), timeZone, dateFormat, trans)
      );
      //nullデータ追加
      selectedItem.forEach(item => {
        const index = yData.findIndex(value => value.name === item);
        if (index !== -1) {
          yData[index].data.push(null);
        }
      });
      //データ存在確認用日時を次のデータ取得時間へと進める
      confirmationDate = confirmationDate + everyMsec;
    };
    for (const stateInfo of pickupDate) {
      //データが欠けている時間帯がある場合、データを追加する
      while (confirmationDate !== new Date(stateInfo['date']).getTime()) {
        addMissingData();
      }
      //時間情報の追加
      xLabel.push(
        translateDate(stateInfo['date'], timeZone, dateFormat, trans)
      );
      //データ部が存在しない場合
      if (stateInfo['states'].length === 0) {
        selectedItem.forEach(item => {
          const index = yData.findIndex(value => value.name === item);
          if (index !== -1) {
            yData[index].data.push(null);
          }
        });
        continue;
      }
      selectedItem.forEach(item => {
        const index = yData.findIndex(value => value.name === item);
        if (index === -1) {
          yData[index].data.push(null);
        } else {
          //データが空欄でないか確認（空欄をグラフ化すると表示がバグる）
          if (
            item in stateInfo.states[targetStateIndex] &&
            stateInfo.states[targetStateIndex][item] !== ''
          ) {
            yData[index].data.push(
              roundOff(Number(stateInfo.states[targetStateIndex][item]), 1)
            );
          } else {
            yData[index].data.push(null);
          }
        }
      });
      //データ存在確認用日時を次のデータ取得時間へと進める
      confirmationDate = confirmationDate + everyMsec;
    }
    //データ取得終了日時までにデータが取得できていない時間帯がある場合、データを追加する
    while (confirmationDate !== new Date(graphEndDate).getTime()) {
      addMissingData();
    }
    return {
      xLabel: xLabel,
      yArrayLine: yData,
      yArrayBarHot: [],
      yArrayBarCool: [],
      yArrayBarEnergy: [],
      yArrayCopLine: [],
    };
  } else if (graphType === 'bar') {
    //消費電力グラフの場合
    let powerConsumptionHotYArray: (number | null)[] = [];
    let powerConsumptionCoolYArray: (number | null)[] = [];

    for (const stateInfo of pickupDate) {
      //時間情報の追加
      xLabel.push(
        translateDate(stateInfo['date'], timeZone, dateFormat, trans)
      );

      //データ部が存在しない場合
      if (stateInfo['states'].length === 0) {
        powerConsumptionHotYArray.push(null);
        powerConsumptionCoolYArray.push(null);
        continue;
      }
      //QtonまたはChillerの場合,暖房消費電力データを追加
      //暖房消費電力が空欄でないか確認（空欄をグラフ化すると表示がバグる）
      if (
        (graphTarget === 'qton' || graphTarget === 'chiller') &&
        'power-consumption' in stateInfo.states[targetStateIndex] &&
        stateInfo['states'][targetStateIndex]['power-consumption']![
          'heating-mode'
        ] !== ''
      ) {
        powerConsumptionHotYArray.push(
          Math.round(
            Number(
              stateInfo.states[targetStateIndex]['power-consumption']![
                'heating-mode'
              ]
            ) / 1000
          )
        );
      } else {
        powerConsumptionHotYArray.push(null);
      }

      //HCCV(Q)またはChillerの場合、冷房消費電力データを追加
      //冷房消費電力が空欄出ないか確認（空欄をグラフ化すると表示がバグる）
      if (
        (graphTarget === 'hyozan' ||
          graphTarget === 'modbus' ||
          graphTarget === 'chiller') &&
        'power-consumption' in stateInfo.states[targetStateIndex] &&
        stateInfo['states'][targetStateIndex]['power-consumption']![
          'cooling-mode'
        ] !== ''
      ) {
        powerConsumptionCoolYArray.push(
          Math.round(
            Number(
              stateInfo.states[targetStateIndex]['power-consumption']![
                'cooling-mode'
              ]
            ) / 1000
          )
        );
      } else {
        powerConsumptionCoolYArray.push(null);
      }
    }

    return {
      xLabel: xLabel,
      yArrayLine: [],
      yArrayBarHot: [
        {name: 'power-consumption-heating', data: powerConsumptionHotYArray},
      ],
      yArrayBarCool: [
        {name: 'power-consumption-cooling', data: powerConsumptionCoolYArray},
      ],
      yArrayBarEnergy: [],
      yArrayCopLine: [],
    };
  }
  //熱量・COPグラフの場合
  //熱量グラフデータの作成
  let energyAmountYArray: (number | null)[] = [];
  for (const stateInfo of pickupDate) {
    //時間情報の追加
    xLabel.push(translateDate(stateInfo['date'], timeZone, dateFormat, trans));
    //データ部が存在しない場合
    if (stateInfo['states'].length === 0) {
      energyAmountYArray.push(null);
      continue;
    }
    //データが空欄でないか確認（空欄をグラフ化すると表示がバグる）
    const energyAmount = stateInfo['states'][targetStateIndex]['energy-amount'];
    if (
      energyAmount !== undefined &&
      (energyAmount['cooling-mode'] !== '' ||
        energyAmount['heating-mode'] !== '')
    ) {
      energyAmountYArray.push(
        Math.round(
          (Number(energyAmount['cooling-mode']) +
            Number(energyAmount['heating-mode'])) /
            1000
        )
      );
      // energyAmountYArray.push(Number(Math.round(Math.random() * (35 - 1) + 1))); //Debug用コード
    } else {
      energyAmountYArray.push(null);
    }
  }
  //COPグラフデータ作成
  let copYArray: (number | null)[] = [];
  for (const stateInfo of pickupDate) {
    //データ部が存在しない場合
    if (stateInfo['states'].length === 0) {
      copYArray.push(null);
      continue;
    } else {
      //データが空欄でないか確認（空欄をグラフ化すると表示がバグる）
      if (
        'cop' in stateInfo.states[targetStateIndex] &&
        stateInfo.states[targetStateIndex]['cop'] !== ''
      ) {
        copYArray.push(
          Math.round(Number(stateInfo.states[targetStateIndex]['cop']) * 10) /
            10
        );
      } else {
        copYArray.push(null);
      }
    }
  }
  //折れ線グラフの配列の要素が全てnullの場合、表示がバグるため空配列とする
  if (copYArray.filter(value => value === null).length === copYArray.length) {
    copYArray = [];
  }

  return {
    xLabel: xLabel,
    yArrayLine: [],
    yArrayBarHot: [],
    yArrayBarCool: [],
    yArrayBarEnergy: [{name: 'energy-amount', data: energyAmountYArray}],
    yArrayCopLine: [{name: 'cop', data: copYArray}],
  };
};

/**
 * 状態値を検索するために必要なパラメータを作成
 * @param graphType グラフの種類
 * @param dateRange グラフ日付範囲
 * @returns パラメータ
 */
const createParameter = (
  graphType: GraphFormType,
  dateRange: GraphDataRange
) => {
  let distance; //グラフのx軸の刻み
  let range = 24; //検索データ範囲 初期値：1日
  let dateFormat = 'HH'; //グラフの軸表示フォーマット 初期値：1日
  if (graphType === 'line') {
    //1日or1週間の切替
    if (dateRange === 'day') {
      distance = 5;
      range = 24 * (60 / distance);
      dateFormat = 'HH:mm';
    } else {
      distance = 30;
      range = 7 * 24 * (60 / distance);
      dateFormat = 'MMM.dd';
    }
  } else {
    //1日or1週間の切替
    if (dateRange === 'day') {
      distance = 60;
      range = 24 * (60 / distance);
      dateFormat = 'HH:mm';
    } else {
      distance = 1440;
      range = 7;
      dateFormat = 'MMM.dd';
    }
  }

  return {
    dateDistance: distance,
    dateRange: range,
    dateFormat: dateFormat,
  };
};

/**
 * Hyozan室内機のグラフ描画用データを作成する
 * @param {GraphFormType} graphType  グラフの種類
 * @param {GraphDataRange} dateRange グラフの日付範囲
 * @param {string} graphTargetID 描画対象のID
 * @param {string} timeZone タイムゾーン
 * @param {apiIF.res.getStatesSitesRt} rtStateData 状態値
 * @param {MakeHyozanIUGraphDataArgIF} selectedItem グラフに描画する項目
 *
 * @returns {
 *  xLabel: string[],
 *  yArrayBar: [],
 *  yArrayLine: {
 *    name: keyof apiIF.resParameters.rtIUStates;
 *    data: (number|null)[]
 *  }[]
 * } グラフ描画用データ
 */
export const makeHyozanIUGraphData = ({
  graphType,
  dateRange,
  graphTargetID,
  timeZone,
  graphStartDate,
  graphEndDate,
  rtStateData,
  selectedItem,
  trans,
}: MakeHyozanIUGraphDataArgIF) => {
  //棒グラフが対応していない
  if (graphType === 'bar') {
    return resultForNoData;
  }
  //どの空調機が選択された判定
  if (rtStateData === null || rtStateData['rt-states'].length === 0) {
    return resultForNoData;
  }
  const targetStateIndex = rtStateData['rt-states'][0][
    'indoor-unit-states'
  ].findIndex(state => {
    return state['unit-id'] === graphTargetID;
  });
  if (targetStateIndex === -1) {
    return resultForNoData;
  }

  //必要なパラメータを作成
  const {dateFormat} = createParameter(graphType, dateRange);

  //グラフ表示に必要なデータのみに絞った配列
  let pickupDate = rtStateData['rt-states'].map(info => {
    return {
      'date': info.date,
      'indoor-unit-states': info['indoor-unit-states'],
    };
  });
  //グラフデータの先頭がグラフ取得開始日でない場合は、配列を反転する
  if (pickupDate[0].date !== graphStartDate) {
    pickupDate.reverse();
  }

  //グラフ作成ライブラリが読み取れる形に整形
  let xLabel = [];
  let yData = selectedItem.map(item => ({
    name: item,
    data: [] as (number | null)[],
  }));
  /** データ存在確認用の日時（unixTime） */
  let confirmationDate = new Date(graphStartDate).getTime();
  /** 温度グラフのデータ取得間隔（msec） */
  const everyMsec =
    dateRange === 'day'
      ? graph.TREND_REPORTING_EVERY_MSEC_DAY
      : graph.TREND_REPORTING_EVERY_MSEC_WEEK;
  /** 時間情報とnullデータを追加し、確認用の日時をデータ取得間隔分進める処理 */
  const addMissingData = () => {
    //時間情報の追加
    xLabel.push(
      translateDate(new Date(confirmationDate), timeZone, dateFormat, trans)
    );
    //nullデータ追加
    selectedItem.forEach(item => {
      const index = yData.findIndex(value => value.name === item);
      if (index !== -1) {
        yData[index].data.push(null);
      }
    });
    //データ存在確認用日時を次のデータ取得時間へと進める
    confirmationDate = confirmationDate + everyMsec;
  };
  for (const stateInfo of pickupDate) {
    //データが欠けている時間帯がある場合、データを追加する
    while (confirmationDate !== new Date(stateInfo['date']).getTime()) {
      addMissingData();
    }
    //時間情報の追加
    xLabel.push(translateDate(stateInfo['date'], timeZone, dateFormat, trans));
    //データ部が存在しない場合
    if (stateInfo['indoor-unit-states'].length === 0) {
      selectedItem.forEach(item => {
        const index = yData.findIndex(value => value.name === item);
        if (index !== -1) {
          yData[index].data.push(null);
        }
      });
      continue;
    }
    selectedItem.forEach(item => {
      const index = yData.findIndex(value => value.name === item);
      if (index === -1) {
        yData[index].data.push(null);
      } else {
        //データが空欄でないか確認（空欄をグラフ化すると表示がバグる）
        if (stateInfo['indoor-unit-states'][targetStateIndex][item] !== '') {
          yData[index].data.push(
            Number(stateInfo['indoor-unit-states'][targetStateIndex][item])
          );
        } else {
          yData[index].data.push(null);
        }
      }
    });
    //データ存在確認用日時を次のデータ取得時間へと進める
    confirmationDate = confirmationDate + everyMsec;
  }
  //データ取得終了日時までにデータが取得できていない時間帯がある場合、データを追加する
  while (confirmationDate !== new Date(graphEndDate).getTime()) {
    addMissingData();
  }
  return {
    xLabel: xLabel,
    yArrayBar: [],
    yArrayLine: yData,
  };
};
